/**
 * A tiny mono tag that sits inside something else — the field group in a field
 * chip ("ORDERS"), the source under a person ("Upload"), "new" beside a name.
 *
 * ## WHY NOT A BADGE
 *
 * `Badge` is a status chip: a bordered pill that stands on its own in a row
 * and says what state something is in. A tag is smaller, square-cornered and
 * borderless, because it lives *inside* another control and a second border
 * there reads as a second control. The concept had five of these
 * (`.sb-grouptag*`, `.sb-srctag*`, `.sb-derived-tag`, `.sb-newtag`,
 * `.sb-pick-custom`); they become one, with the tone as the only variable.
 *
 * ## WHAT EACH TONE MAY MEAN
 *
 *   - `neutral` — the plain default (a profile field).
 *   - `brand` — something Wend made (a derived field). `brand-ink-strong` on
 *     `brand-b/32`, about 9:1.
 *   - `ident-a` … `ident-d` — an identity: a source or a field group, from
 *     `lib/identity.ts`. Never a verdict; the word inside is the meaning and
 *     the colour only helps the eye find it again. 5.57:1 at worst, on a
 *     bare surface.
 *   - `good` — **only** for a real status word, such as "new" or "entered".
 *     It is not a pretty green for a label; the concept painted Orders green
 *     and sat it beside a "known for 100%" in the same green, and the reader
 *     could not tell which of the two was the good news.
 *
 * ## INSIDE A CHIP, THE WASH DARKENS
 *
 * A tag measured on a bare surface is not the tag a reader meets inside a
 * `FieldChip`: there it sits on the chip's brand-a/16 fill (brand-a/24 on
 * hover), inside a mapping row, inside a card, and every one of those layers
 * lightens what is under the text. The neutral tag's muted-on-white/.08
 * measured 4.12:1 at rest and 3.74:1 on hover in that stack (slice 66 review),
 * on shipped words like PROFILE. So `inset` swaps the lightening wash for a
 * darkening one, `bg-black/30`, and keeps the tone's ink: worst case 5.88:1
 * (muted, chip hover in a plain row), identity inks 6.58:1 and up. `brand`
 * keeps its own fill, which already clears 6.7:1 there. verify-ui-suite
 * composites the whole stack and measures it.
 *
 * ## SIZE
 *
 * 0.625rem (10px), not the concept's 0.58rem: these are meaning-bearing words
 * (SNAPSHOT, PROFILE), and 9.3px uppercase mono was below a sensible floor.
 *
 * ## THE HOLE IT LEAVES
 *
 * The type allows `good` with any text. Nothing checks that the word inside is
 * a status word; that is on the caller, and on review.
 */
import type { HTMLAttributes } from 'react';

import { cn } from '../lib/cn';
import type { IdentTone } from '../lib/identity';

const TAG_TONE = {
  neutral: 'bg-white/[0.08] text-muted',
  brand: 'bg-brand-b/32 text-brand-ink-strong',
  'ident-a': 'bg-ident-a/16 text-ident-a',
  'ident-b': 'bg-ident-b/16 text-ident-b',
  'ident-c': 'bg-ident-c/16 text-ident-c',
  'ident-d': 'bg-ident-d/16 text-ident-d',
  good: 'bg-good/16 text-good',
} as const satisfies Readonly<Record<'neutral' | 'brand' | IdentTone | 'good', string>>;

export type TagTone = keyof typeof TAG_TONE;

/** The fills for a tag that sits inside a tinted chip. See INSIDE A CHIP. */
const TAG_TONE_INSET = {
  ...TAG_TONE,
  neutral: 'bg-black/30 text-muted',
  'ident-a': 'bg-black/30 text-ident-a',
  'ident-b': 'bg-black/30 text-ident-b',
  'ident-c': 'bg-black/30 text-ident-c',
  'ident-d': 'bg-black/30 text-ident-d',
  good: 'bg-black/30 text-good',
} as const satisfies Readonly<Record<TagTone, string>>;

export interface TagProps extends HTMLAttributes<HTMLSpanElement> {
  tone: TagTone;
  /** True when the tag sits inside a tinted chip (FieldChip, a chip Select). */
  inset?: boolean;
}

export function Tag({ tone, inset = false, className, ...rest }: TagProps) {
  return (
    <span
      className={cn(
        'inline-block shrink-0 whitespace-nowrap rounded-xs px-[5px] py-[2px]',
        'font-mono text-[0.625rem] font-medium uppercase leading-none tracking-[0.08em]',
        (inset ? TAG_TONE_INSET : TAG_TONE)[tone],
        className,
      )}
      {...rest}
    />
  );
}
